import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = ["downloadButton", "loadingIndicator", "errorMessage", "warningMessage"]
  static values = {
    interval: { type: Number, default: 2000 },
    maxAttempts: { type: Number, default: 150 } // 5 minutes max (150 * 2s)
  }

  connect() {
    this.attempts = 0
    this.polling = false
    this.reportId = null
    this.downloadStarted = false
    this.currentLocale = window.location.pathname.split('/')[1] || 'en'
    this.problematicOrderIds = []
  }

  disconnect() {
    this.stopPolling()
  }

  async startDownload(event) {
    event.preventDefault()

    if (this.polling) return

    // Récupérer le formulaire parent
    const form = event.target.closest('form')
    if (!form) {
      console.error('Form not found')
      return
    }

    // Récupérer les order_ids filtrés depuis le champ caché
    const filteredOrdersInput = form.querySelector('input[name="filtered_orders_ids"]')

    if (!filteredOrdersInput || !filteredOrdersInput.value) {
      this.showError('Veuillez d\'abord filtrer les commandes')
      return
    }

    const orderIds = filteredOrdersInput.value.split(',').filter(id => id.trim() !== '')

    if (orderIds.length === 0) {
      this.showError('Veuillez d\'abord filtrer les commandes')
      return
    }

    this.hideError()
    this.hideWarning()
    this.showLoading()

    try {
      // Récupérer le token CSRF
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      // Créer les paramètres pour la requête
      const params = new URLSearchParams()
      orderIds.forEach(id => params.append('record_ids[]', id))
      params.append('record_type', 'BaseQuotation')
      params.append('name', 'buyer_invoice_uploads')
      params.append('locale', this.currentLocale)

      const response = await fetch(`/${this.currentLocale}/merge_pdfs.json?${params.toString()}`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorMessage
        } catch (e) {
          // Si on ne peut pas parser le JSON, on garde le message par défaut
          console.error('Could not parse error response as JSON:', e)
        }
        throw new Error(errorMessage)
      }

      let data
      try {
        data = await response.json()
      } catch (e) {
        console.error('Could not parse response as JSON:', e)
        throw new Error('La réponse du serveur n\'est pas au format attendu')
      }

      if (data.status === 'success' && data.report_id) {
        this.reportId = data.report_id
        this.problematicOrderIds = data.problematic_order_ids || []

        // Afficher le warning s'il y en a un
        if (data.warning) {
          this.showWarning(data.warning)
        }

        this.startPolling()
      } else if (data.status === 'error') {
        throw new Error(data.message || 'Download failed')
      }
    } catch (error) {
      console.error('Download error:', error)
      this.showError(error.message || 'Erreur lors du démarrage du téléchargement')
      this.hideLoading()
    }
  }

  startPolling() {
    if (this.polling || !this.reportId) return

    this.polling = true
    this.attempts = 0
    this.pollerId = setInterval(() => this.checkStatus(), this.intervalValue)
  }

  stopPolling() {
    if (this.pollerId) {
      clearInterval(this.pollerId)
      this.pollerId = null
    }
    this.polling = false
  }

  async checkStatus() {
    // Éviter les appels multiples si le download a déjà commencé
    if (this.downloadStarted) {
      return
    }

    if (this.attempts >= this.maxAttemptsValue) {
      this.stopPolling()
      this.showError("Le traitement prend trop de temps. Veuillez réessayer plus tard.")
      this.hideLoading()
      return
    }

    this.attempts++

    try {
      const response = await fetch(`/${this.currentLocale}/merge_pdfs/${this.reportId}/status`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Erreur réseau")

      const data = await response.json()

      if (data.status === "ready" && !this.downloadStarted) {
        // Marquer le download comme commencé pour éviter les appels multiples
        this.downloadStarted = true
        // Arrêter le polling immédiatement
        this.stopPolling()
        // Mettre à jour le warning avec le lien vers admin report
        this.updateWarningAfterDownload()
        // Télécharger le fichier
        this.downloadFile()
      } else if (data.status === "error") {
        const errorMessage = data.message || "Une erreur s'est produite lors du traitement."
        this.showError(errorMessage)
        this.stopPolling()
        this.hideLoading()
      }
      // Si status === "pending", continuer le polling
    } catch (error) {
      console.error("Polling error:", error)
      this.showError("Une erreur de communication s'est produite.")
      this.stopPolling()
      this.hideLoading()
    }
  }

  downloadFile() {
    // Télécharger directement le fichier via un lien avec la locale
    const downloadUrl = `/${this.currentLocale}/merge_pdfs/${this.reportId}/download`

    const link = document.createElement('a')
    link.href = downloadUrl
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    // Nettoyer et cacher le loading
    setTimeout(() => {
      document.body.removeChild(link)
      this.hideLoading()
    }, 100)
  }

  showLoading() {
    if (this.hasDownloadButtonTarget) {
      this.downloadButtonTarget.classList.add('hidden')
    }
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasDownloadButtonTarget) {
      this.downloadButtonTarget.classList.remove('hidden')
    }
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
      this.errorMessageTarget.textContent = ''
    }
  }

  showWarning(message) {
    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.textContent = message
      this.warningMessageTarget.classList.remove('hidden')
    }
  }

  hideWarning() {
    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.classList.add('hidden')
      this.warningMessageTarget.textContent = ''
    }
  }

  updateWarningAfterDownload() {
    if (this.problematicOrderIds.length > 0 && this.hasWarningMessageTarget) {
      const count = this.problematicOrderIds.length
      const reportUrl = `/${this.currentLocale}/admin/admin_reports/${this.reportId}`
      const message = `Attention ${count} PDF ont été exclus. Voir le rapport pour plus de détails : `

      this.warningMessageTarget.innerHTML = ''
      this.warningMessageTarget.textContent = message

      const link = document.createElement('a')
      link.href = reportUrl
      link.textContent = `Admin Report #${this.reportId}`
      link.className = 'underline text-blue-600 hover:text-blue-800'
      link.target = '_blank'

      this.warningMessageTarget.appendChild(link)
      this.warningMessageTarget.classList.remove('hidden')
    }
  }
}
